import { useEffect, useMemo, useState, useCallback } from 'react';
import { useBackForward } from '../../useBackForward';
import { Button, Callout, Flex, Text, TextField } from '@dodilio/ui-design-lib';
import { InviteTeam } from './InviteTeam';
import { auth } from '../../auth';
import { ServiceRegister } from '../../api';
import { alreadyExists, errorText } from '../../errorText';
import { forgetReturnTo, returnTo } from '../../returnTo';
import { leaveWithSession } from '../../handoff';
import { useReferral } from '../../useReferral';
import { forgetReferral } from '../../referral';
import { ORG_NAME_MAX, alternativeHandle, countedLength, fitHandle, orgNameProblem, suggestOrgName, toOrgName } from '../../orgName';
import { forgetClientInvite, inviteErrorText, readClientInvite } from '../../clientInvite';
import { whoFromToken } from '../../session';

/*
 * Name the organization. One field, the name people read, guessed from who is
 * signing up. The handle (iam's organization name: in service accounts and app
 * addresses, so it can't change) follows it inside iam's budget, and can be
 * edited, after which it stops following. Free or taken is checked as it
 * settles. Then, optionally, invite teammates.
 */

/** How long a notice ("the referral code wasn't applied") stays before moving on. */
const NOTICE_MS = 4000;
const DISPLAY_MIN = 2;
const DISPLAY_MAX = 64;
const CHECK_AFTER_MS = 400;

type Availability = { state: 'idle' | 'checking' | 'unknown' } | { state: 'free' } | { state: 'taken'; reason?: string };

/** Free or taken, asked once the handle has settled. Unknown when iam can't say yet (the submit decides). */
function useAvailability(handle: string, valid: boolean): Availability {
    const [a, setA] = useState<Availability>({ state: 'idle' });
    useEffect(() => {
        if (!valid) { setA({ state: 'idle' }); return; }
        setA({ state: 'checking' });
        let alive = true;
        const t = window.setTimeout(() => {
            void ServiceRegister.checkOrganizationName(handle, auth.getAccessToken() || '').then((r) => {
                if (!alive) return;
                setA(r === null ? { state: 'unknown' } : r.available ? { state: 'free' } : { state: 'taken', reason: r.reason });
            });
        }, CHECK_AFTER_MS);
        return () => { alive = false; window.clearTimeout(t); };
    }, [handle, valid]);
    return a;
}

export const CreateOrgForm = () => {
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [message, setMessage] = useState('');
    const [notice, setNotice] = useState('');
    // Back from the console mid-create: the button works again (a created organization stays created).
    useBackForward(useCallback(() => setStatus((st) => (st === 'loading' ? 'idle' : st)), []));
    const referral = useReferral();
    // A partner's client invitation: the new organization accepts it, and a
    // referral code is not sent (the invitation already names the partner).
    const [invite] = useState(readClientInvite);
    const [created, setCreated] = useState<{ id: string; handle: string } | null>(null);

    const who = useMemo(() => whoFromToken(auth.getAccessToken()), []);
    const [display, setDisplay] = useState(() => suggestOrgName(who?.email, who?.name));
    const [handle, setHandle] = useState(() => fitHandle(display));
    const [handleEdited, setHandleEdited] = useState(false);
    const [editing, setEditing] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // The handle follows the name until it's edited.
    useEffect(() => {
        if (!handleEdited) setHandle(fitHandle(display));
    }, [display, handleEdited]);

    const displayProblem = display.trim().length < DISPLAY_MIN ? `Use at least ${DISPLAY_MIN} characters.` : undefined;
    const handleProblem = orgNameProblem(handle);
    const availability = useAvailability(handle, !handleProblem);
    // iam said taken when creating (before the name check was live): the same note as the check's.
    const [takenOnSubmit, setTakenOnSubmit] = useState('');
    const counted = countedLength(handle);
    const taken = !!handle && (availability.state === 'taken' || takenOnSubmit === handle);
    // A name in another script (中文公司, 日本) gives no Latin address to derive: ask for one.
    const needsLatin = !handle && display.trim().length >= DISPLAY_MIN;
    const blocked = !!displayProblem || !!handleProblem || taken;

    const leave = async (organizationName: string) => {
        const target = returnTo(import.meta.env.VITE_DEFAULT_REDIRECT);
        forgetReturnTo();
        await leaveWithSession({ target, organizationName });
    };

    const create = async () => {
        setSubmitted(true);
        if (needsLatin) { setEditing(true); return; }
        if (blocked) return;
        const token = auth.getAccessToken();
        if (!token) {
            setStatus('error');
            setMessage('Your session has ended. Start again from the sign-up page.');
            return;
        }
        setStatus('loading');
        try {
            const res = await ServiceRegister.completeSignup(token, {
                organizationName: handle,
                displayName: display.trim(),
                // Only sent when there is one; a code that does not apply never
                // fails signup, it comes back as referralCodeRejected.
                ...(referral.code && !invite ? { referralCode: referral.code } : {}),
            });
            forgetReferral();
            let inviteNotice = '';
            if (invite && res?.organizationId) {
                try {
                    await ServiceRegister.acceptClientInvitation(invite.token, res.organizationId, auth.getAccessToken() || token);
                    forgetClientInvite();
                } catch (e) {
                    // The organization exists either way; say why the partner link didn't happen.
                    inviteNotice = `Your organization was created, but the invitation wasn't accepted: ${inviteErrorText(e, invite.email)}`;
                }
            }
            // A fresh token carries the new organization; without it the destination
            // sees none and sends them back here. Its failure isn't a failed signup.
            await auth.forceRefresh().catch(() => false);
            setStatus('success');
            if (inviteNotice || (!invite && referral.code && res?.referralCodeRejected)) {
                setNotice(inviteNotice || "Your organization was created. The referral code wasn't applied.");
                await new Promise((r) => setTimeout(r, NOTICE_MS));
            }
            // A partner's client goes straight on; anyone else may invite their team first.
            if (invite || !res?.organizationId) await leave(handle);
            else setCreated({ id: res.organizationId, handle });
        } catch (err) {
            // Taken is about the handle: said beside it, with another to try, not as a banner about "the name".
            if (alreadyExists(err)) { setTakenOnSubmit(handle); setStatus('idle'); return; }
            setStatus('error');
            setMessage(errorText(err, 'org'));
        }
    };

    if (created) {
        return <InviteTeam organizationId={created.id} onDone={() => void leave(created.handle)} />;
    }

    const handleNote = editing
        ? undefined
        : needsLatin
            ? (
                <Text size="1" color="gray" role="status">
                    Add a short address using Latin letters or digits (e.g. acme).{' '}
                    <button type="button" className="linkButton" onClick={() => setEditing(true)}>Add it</button>
                </Text>
            )
        : handleProblem && (submitted || handleEdited)
            ? <Text size="1" className="field-error" role="alert">{handleProblem}</Text>
            : taken
                ? (
                    <Text size="1" className="field-error" role="alert">
                        {(availability.state === 'taken' && availability.reason) || `That address (${handle}) is taken.`}{' '}
                        <button type="button" className="linkButton" onClick={() => { setHandle(alternativeHandle(handle)); setHandleEdited(true); }}>
                            Try {alternativeHandle(handle)}
                        </button>
                    </Text>
                )
                : availability.state === 'free'
                    ? <Text size="1" color="green">Available</Text>
                    : availability.state === 'checking'
                        ? <Text size="1" color="gray">Checking…</Text>
                        : null;

    return (
        <form onSubmit={(e) => { e.preventDefault(); void create(); }} noValidate>
            {(status === 'error' || status === 'success') && (
                <Callout.Root mb="4" size="1" className="api-banner" variant="soft" color={status === 'success' ? 'green' : 'tomato'} role={status === 'error' ? 'alert' : 'status'}>
                    <Callout.Text>{status === 'success' ? 'Organization created.' : message}</Callout.Text>
                </Callout.Root>
            )}
            {notice && (
                <Callout.Root mb="4" size="1" className="api-banner" variant="soft" color="gray" role="status">
                    <Callout.Text>{notice}</Callout.Text>
                </Callout.Root>
            )}
            {status !== 'success' && (
                <Flex direction="column" gap="3">
                    <TextField.Root
                        value={display}
                        onChange={(e) => { setDisplay(e.target.value.slice(0, DISPLAY_MAX)); if (status === 'error') setStatus('idle'); }}
                        size="2"
                        variant="surface"
                        autoFocus
                        placeholder="Acme Inc."
                        aria-label="Organization name"
                        color={displayProblem && submitted ? 'tomato' : 'gray'}
                    >
                        <TextField.Slot className="fieldLabel"><Text size="1">Organization name</Text></TextField.Slot>
                    </TextField.Root>
                    {displayProblem && submitted
                        ? <Text size="1" className="field-error" role="alert">{displayProblem}</Text>
                        : <Text size="1" color="gray" mt="-1">How people see it. You can change this later.</Text>}

                    <Flex direction="column" gap="1" className="handleBox">
                        <Flex align="center" justify="between" gap="3">
                            <Text size="1" color="gray">Handle</Text>
                            <Text size="1" color={counted > ORG_NAME_MAX ? 'tomato' : 'gray'} aria-label={`${counted} of ${ORG_NAME_MAX}; hyphens count as two`}>
                                {counted} / {ORG_NAME_MAX} · hyphens count as 2
                            </Text>
                        </Flex>
                        {editing ? (
                            <TextField.Root
                                value={handle}
                                onChange={(e) => { setHandle(toOrgName(e.target.value)); setHandleEdited(true); if (status === 'error') setStatus('idle'); }}
                                onBlur={() => { setHandle((h) => h.replace(/-+$/, '')); setEditing(false); }}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setEditing(false); } }}
                                size="2"
                                variant="surface"
                                autoFocus
                                aria-label="Handle"
                                color={handleProblem ? 'tomato' : 'gray'}
                            />
                        ) : (
                            <Flex align="center" gap="2">
                                <code className="handleChip">{handle || '—'}</code>
                                <button type="button" className="linkButton" onClick={() => setEditing(true)} aria-label="Edit handle">Edit</button>
                            </Flex>
                        )}
                        {handleNote}
                        <Text size="1" color="gray">
                            Used in your app addresses, like <code>web-{(handle || 'acme').replace(/-/g, '--')}-8080</code>. Your handle can&apos;t be changed later.
                        </Text>
                    </Flex>

                    {invite?.partnerName && (
                        <Text size="1" color="gray" role="status">
                            Working with <Text weight="medium" color="gray">{invite.partnerName}</Text>: this organization becomes their client.
                        </Text>
                    )}
                    {!invite && referral.check?.kind === 'valid' && (
                        <Text size="1" color="gray" role="status">
                            {referral.check.partnerDisplayName
                                ? <>Referred by <Text weight="medium" color="gray">{referral.check.partnerDisplayName}</Text></>
                                : 'Referred by a Dodil partner'}
                        </Text>
                    )}
                    {!invite && referral.check?.kind === 'invalid' && (
                        <Text size="1" color="gray">This referral code isn&apos;t valid.</Text>
                    )}

                    <Button radius="full" type="submit" size="3" mt="4" disabled={status === 'loading' || (submitted && blocked)} loading={status === 'loading'}>
                        Create organization
                    </Button>
                </Flex>
            )}
        </form>
    );
};
